import { PageHero } from "@/components/page-hero";
import { PageLayout } from "@/components/page-layout";
import { client } from "@/utils/orpc";
import { breadcrumbSchema, datasetSchema, webPageSchema } from "@/utils/schema";
import { jsonLdScript, seo } from "@/utils/seo";
import type { HiringTrendsData } from "@super-resume-new/api/lib/jobs";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@super-resume-new/ui/components/card";
import { Input } from "@super-resume-new/ui/components/input";
import { InputGroup, InputGroupAddon } from "@super-resume-new/ui/components/input-group";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Award,
  BarChart3,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  DollarSign,
  Flame,
  Globe2,
  GraduationCap,
  Laptop,
  Search,
  Sparkles,
  TrendingUp,
  Wand2,
  Wifi,
} from "lucide-react";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/jobs/tech-hiring-trends")({
  loader: async (): Promise<HiringTrendsData | null> => {
    try {
      return await client.jobs.hiringTrends();
    } catch (err) {
      console.error("[hiring-trends] failed to load hiring trends:", err);
      return null;
    }
  },
  headers: () => ({
    "cache-control": "public, s-maxage=86400, max-age=0, must-revalidate",
  }),
  head: ({ loaderData }) => {
    const data = loaderData as HiringTrendsData | null;
    const title = "Tech Hiring Trends & In-Demand Skills 2026 | JobCues";
    const description =
      "Explore live 2026 tech hiring trends. Track the most in-demand programming skills, active employers, remote work stats, and compensation benchmarks from verified jobs.";
    const head = seo({
      title,
      description,
      path: "/jobs/tech-hiring-trends",
    });

    const breadcrumbs = breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Jobs", path: "/jobs" },
      { name: "Tech Hiring Trends", path: "/jobs/tech-hiring-trends" },
    ]);

    const pageSchema = webPageSchema({
      name: title,
      description,
      path: "/jobs/tech-hiring-trends",
      dateModified: data?.updatedAt ? new Date(data.updatedAt).toISOString() : undefined,
    });

    const dataset = datasetSchema({
      name: "Live Tech Hiring Trends and In-Demand Skills Index",
      description:
        "Real-time tech hiring statistics and skills demand index aggregated directly from verified employer career boards.",
      path: "/jobs/tech-hiring-trends",
      keywords: [
        "tech hiring trends",
        "software engineering skills",
        "programming languages in demand",
        "remote tech jobs",
        "tech compensation benchmarks",
      ],
      dateModified: data?.updatedAt ? new Date(data.updatedAt).toISOString() : undefined,
    });

    return {
      ...head,
      scripts: [
        jsonLdScript(breadcrumbs),
        jsonLdScript(pageSchema),
        jsonLdScript(dataset),
      ],
    };
  },
  component: HiringTrendsPageComponent,
});

function formatDate(date: string | Date | undefined | null): string {
  if (!date) return "Recently";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "Recently";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function HiringTrendsPageComponent() {
  const data = Route.useLoaderData() as HiringTrendsData | null;
  const [skillSearch, setSkillSearch] = useState("");
  const [selectedRoleGroup, setSelectedRoleGroup] = useState<"all" | "tech">("all");

  const summary = data?.summary ?? {
    totalActiveJobs: 0,
    totalCompanies: 0,
    remotePercentage: 0,
    hybridPercentage: 0,
    topSkill: null,
    topCompany: null,
    salaryTransparencyPercentage: 0,
  };

  const allSkills = data?.skills ?? [];
  const filteredSkills = useMemo(() => {
    if (!skillSearch.trim()) return allSkills;
    const q = skillSearch.toLowerCase().trim();
    return allSkills.filter((s) => s.name.toLowerCase().includes(q));
  }, [allSkills, skillSearch]);

  const roles = useMemo(() => {
    const rawRoles = data?.roles ?? [];
    if (selectedRoleGroup === "tech") {
      const techKeys = new Set([
        "ai_ml",
        "swe",
        "cloud_infra",
        "devops_cloud",
        "backend",
        "frontend",
        "fullstack",
        "data",
        "security",
        "qa",
        "mobile",
      ]);
      return rawRoles.filter((r) => techKeys.has(r.key));
    }
    return rawRoles;
  }, [data?.roles, selectedRoleGroup]);

  const maxRoleCount = useMemo(() => {
    if (!roles.length) return 1;
    return Math.max(...roles.map((r) => r.count));
  }, [roles]);

  return (
    <PageLayout id="hiring-trends-content" className="max-w-7xl">
      {/* Page Hero Header */}
      <PageHero
        eyebrow="Tech hiring trends 2026"
        icon={TrendingUp}
        title={
          <>
            <strong>Tech hiring trends</strong>
            <br />
            In-demand skills 2026
          </>
        }
        description="Real-time hiring intelligence aggregated directly from verified employer career boards. Track in-demand engineering stacks, active employers, work styles, and compensation benchmarks."
      >
        {/* CTA Buttons */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button size="lg" render={<Link to="/jobs" />}>
            <Briefcase className="mr-2 size-4" />
            Browse all {summary.totalActiveJobs.toLocaleString()} active jobs
            <ArrowRight className="ml-1 size-3.5" />
          </Button>
          <Button variant="outline" size="lg" render={<Link to="/resume-tailor" />}>
            <Wand2 className="mr-2 size-4 text-violet-500" />
            Tailor Resume for In-Demand Roles
          </Button>
        </div>

        {/* Last Updated Date */}
        <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Calendar className="size-3.5 text-muted-foreground" />
          <span>Last updated: {formatDate(data?.updatedAt)}</span>
        </div>
      </PageHero>

      {/* KPI Highlights Ribbon */}
      <section className="mb-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Card className="border-border/80 bg-card/60 backdrop-blur-xs p-4 transition-all hover:border-border">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Briefcase className="size-3.5 text-violet-500" />
            <span>Active Roles</span>
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-foreground">
            {summary.totalActiveJobs.toLocaleString()}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">Verified positions indexed</div>
        </Card>

        <Card className="border-border/80 bg-card/60 backdrop-blur-xs p-4 transition-all hover:border-border">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Building2 className="size-3.5 text-blue-500" />
            <span>Active Employers</span>
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-foreground">
            {summary.totalCompanies.toLocaleString()}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">Direct career portals</div>
        </Card>

        <Card className="border-border/80 bg-card/60 backdrop-blur-xs p-4 transition-all hover:border-border">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Wifi className="size-3.5 text-emerald-500" />
            <span>Remote Friendly</span>
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-foreground">
            {summary.remotePercentage + summary.hybridPercentage}%
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">Remote or hybrid friendly</div>
        </Card>

        <Card className="border-border/80 bg-card/60 backdrop-blur-xs p-4 transition-all hover:border-border">
          <Link
            to="/jobs"
            search={{ skill: summary.topSkill?.name ?? "Python" }}
            className="group block"
          >
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground group-hover:text-amber-500 transition-colors">
              <Flame className="size-3.5 text-amber-500" />
              <span>#1 Top Skill</span>
            </div>
            <div className="mt-2 truncate text-2xl font-bold tracking-tight text-foreground group-hover:text-brand transition-colors">
              {summary.topSkill?.name ?? "Python"}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {summary.topSkill?.count ?? 0} listings require it
            </div>
          </Link>
        </Card>

        <Card className="col-span-2 sm:col-span-1 border-border/80 bg-card/60 backdrop-blur-xs p-4 transition-all hover:border-border">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <DollarSign className="size-3.5 text-teal-500" />
            <span>Comp Disclosed</span>
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-foreground">
            {summary.salaryTransparencyPercentage}%
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">Include verified salary</div>
        </Card>
      </section>

      {/* Main 2-Column Bento Grid */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Column 1: Skills & Roles in Demand */}
        <div className="flex flex-col gap-6">
          {/* Top Requested Skills */}
          <Card className="border-border/80 bg-card/80 shadow-xs backdrop-blur-xs">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle render={<h2 />} className="flex items-center mb-2 gap-2 text-base font-semibold">
                <BarChart3 className="size-4 text-violet-500" />
                Top In-Demand Skills
              </CardTitle>
              {/* Quick skill filter */}
              <InputGroup>
                <InputGroupAddon>
                  <Search className="size-3.5 text-muted-foreground" />
                </InputGroupAddon>
                <Input
                  type="text"
                  placeholder="Filter skills (e.g. Python, AWS, Docker)..."
                  value={skillSearch}
                  onChange={(e) => setSkillSearch(e.target.value)}
                />
              </InputGroup>
            </CardHeader>

            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2">
                {filteredSkills.slice(0, 24).map((skill) => (
                  <Link
                    key={skill.normalized}
                    to="/jobs"
                    search={{ skill: skill.name }}
                    className="group inline-flex items-center gap-1.5 rounded-lg border border-border/80 bg-card/80 px-2.5 py-1.5 text-xs font-medium text-foreground transition-all hover:border-violet-500/50 hover:bg-violet-500/10 hover:shadow-xs"
                  >
                    <span>{skill.name}</span>
                    <Badge variant="secondary">
                      {skill.count}
                    </Badge>
                  </Link>
                ))}
              </div>

              {filteredSkills.length === 0 ? (
                <p className="py-6 text-center text-xs text-muted-foreground">
                  No skills matching &quot;{skillSearch}&quot;
                </p>
              ) : null}
            </CardContent>
          </Card>

          {/* Roles in Demand */}
          <Card className="border-border/80 bg-card/80 shadow-xs backdrop-blur-xs flex-1">
            <CardHeader className="border-b border-border/50">
              <div className="flex items-center justify-between">
                <CardTitle render={<h2 />} className="flex items-center gap-2 text-base font-semibold">
                  <TrendingUp className="size-4 text-violet-500" />
                   Roles in Highest Demand
                </CardTitle>
                <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/40 p-0.5">
                  <button
                    type="button"
                    onClick={() => setSelectedRoleGroup("all")}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                      selectedRoleGroup === "all"
                        ? "bg-card text-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    All Roles
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedRoleGroup("tech")}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                      selectedRoleGroup === "tech"
                        ? "bg-card text-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Engineering Only
                  </button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-4 space-y-3.5">
              {roles.slice(0, 8).map((role) => {
                const pctOfMax = Math.round((role.count / maxRoleCount) * 100);
                return (
                  <Link
                    key={role.key}
                    to="/jobs"
                    search={{ q: role.name }}
                    className="group block space-y-1.5 transition-opacity hover:opacity-90"
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-foreground group-hover:text-brand transition-colors">
                        {role.name}
                      </span>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span className="font-semibold text-foreground">{role.count}</span>
                        <span className="text-[10px]">({role.percentage}%)</span>
                      </div>
                    </div>

                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted/60">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-violet-800 to-violet-400 transition-all duration-500"
                        style={{ width: `${Math.max(pctOfMax, 6)}%` }}
                      />
                    </div>
                  </Link>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Column 2: Work Style & Active Employers */}
        <div className="flex flex-col gap-6">
          {/* Work Style */}
          <Card className="border-border/80 bg-card/80 shadow-xs backdrop-blur-xs">
            <CardHeader className="border-b border-border/50">
              <CardTitle render={<h2 />} className="flex items-center gap-2 text-base font-semibold">
                <Laptop className="size-4 text-violet-500" />
                Work Style Trends
              </CardTitle>
            </CardHeader>

            <CardContent className="p-4 space-y-4">
              {(data?.workModes ?? []).map((wm) => {
                const colorClass =
                  wm.mode === "remote"
                    ? "from-emerald-500 to-teal-500"
                    : wm.mode === "hybrid"
                      ? "from-blue-500 to-cyan-500"
                      : wm.mode === "onsite"
                        ? "from-amber-500 to-orange-500"
                        : "from-neutral-400 to-neutral-500";

                return (
                  <Link
                    key={wm.mode}
                    to="/jobs"
                    search={wm.mode !== "unstated" ? { workMode: wm.mode as any } : {}}
                    className="group block space-y-1.5 transition-opacity hover:opacity-90"
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 font-medium text-foreground group-hover:text-brand">
                        {wm.mode === "remote" ? (
                          <Wifi className="size-3.5 text-emerald-500" />
                        ) : wm.mode === "hybrid" ? (
                          <Laptop className="size-3.5 text-blue-500" />
                        ) : wm.mode === "onsite" ? (
                          <Building2 className="size-3.5 text-amber-500" />
                        ) : (
                          <Globe2 className="size-3.5 text-neutral-400" />
                        )}
                        {wm.label}
                      </span>
                      <span className="font-semibold tabular-nums text-foreground">{wm.count}</span>
                    </div>

                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted/60">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r ${colorClass} transition-all duration-500`}
                        style={{ width: `${Math.max(wm.percentage, 4)}%` }}
                      />
                    </div>
                  </Link>
                );
              })}
            </CardContent>
          </Card>

          {/* Most Active Companies */}
          <Card className="border-border/80 bg-card/80 shadow-xs backdrop-blur-xs flex-1">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle render={<h2 />} className="flex items-center gap-2 text-base font-semibold">
                <Award className="size-4 text-amber-500" />
                Most Active Employers Hiring Today
              </CardTitle>
            </CardHeader>

            <CardContent className="p-3 space-y-2 overflow-y-auto max-h-[472px]">
              {(data?.companies ?? []).slice(0, 10).map((c) => (
                <Link
                  key={c.company}
                  to="/jobs"
                  search={{ q: c.company }}
                  className="group flex items-center justify-between rounded-lg border border-border/50 bg-background/50 p-2.5 transition-all hover:border-brand/40 hover:bg-accent/40"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className="flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-bold bg-muted text-muted-foreground"
                    >
                      {c.rank}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-xs font-semibold text-foreground group-hover:text-brand transition-colors">
                        {c.company}
                      </div>
                    </div>
                  </div>
                      <div className="text-xs font-medium text-muted-foreground">
                        {c.count} {c.count === 1 ? "job" : "jobs"} listed
                      </div>

                </Link>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Additional Intelligence Section: Seniority & Salary Transparency */}
      <section className="mt-12">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
              Career level & compensation dynamics
            </h2>
            <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
              Explore entry barriers, career progression volume, and published salary transparency.
            </p>
          </div>
          <Button variant="outline" size="sm" render={<Link to="/jobs" search={{ hasSalary: true }} />}>
            <DollarSign className="mr-1.5 size-3.5 text-teal-500" />
            View salary-transparent jobs
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Seniority Distribution */}
          <Card className="border-border/80 bg-card/80 p-5 shadow-xs backdrop-blur-xs">
            <h3 className="flex items-center gap-2 font-semibold text-foreground">
              <GraduationCap className="size-4 text-violet-500" />
              <span>Seniority & Experience Landscape</span>
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Volume of openings by target career stage and years of experience.
            </p>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {(data?.seniorities ?? []).map((s) => (
                <Link
                  key={s.seniority}
                  to="/jobs"
                  search={s.seniority !== "unspecified" ? { seniority: s.seniority as any } : {}}
                  className="group rounded-xl border border-border/60 bg-background/50 p-3 transition-all hover:border-brand/40 hover:bg-accent/40"
                >
                  <div className="text-[11px] font-medium text-muted-foreground group-hover:text-foreground">
                    {s.label}
                  </div>
                  <div className="mt-1.5 flex items-baseline gap-1.5">
                    <span className="text-xl font-bold tracking-tight text-foreground">{s.count}</span>
                    <span className="text-[10px] text-muted-foreground font-semibold">({s.percentage}%)</span>
                  </div>
                  <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted/60">
                    <div
                      className="h-full rounded-full bg-violet-500"
                      style={{ width: `${Math.max(s.percentage, 5)}%` }}
                    />
                  </div>
                </Link>
              ))}
            </div>
          </Card>

          {/* Salary Transparency & Range Benchmarks */}
          <Card className="border-border/80 bg-card/80 p-5 shadow-xs backdrop-blur-xs flex flex-col justify-between">
            <div>
              <h3 className="flex items-center gap-2 font-semibold text-foreground">
                <DollarSign className="size-4 text-teal-500" />
                <span>Salary Transparency & Base Benchmarks</span>
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Compensation stats extracted from postings with disclosed ranges.
              </p>

              <div className="mt-5 grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-border/60 bg-background/50 p-4">
                  <div className="text-xs font-medium text-muted-foreground">Average Min Base</div>
                  <div className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                    {data?.salaryStats.avgMin ? `$${Math.round(data.salaryStats.avgMin / 1000)}k` : "N/A"}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">Typical bottom of range</div>
                </div>

                <div className="rounded-xl border border-border/60 bg-background/50 p-4">
                  <div className="text-xs font-medium text-muted-foreground">Average Max Base</div>
                  <div className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                    {data?.salaryStats.avgMax ? `$${Math.round(data.salaryStats.avgMax / 1000)}k` : "N/A"}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">Typical top of range</div>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-teal-500/20 bg-teal-500/5 p-3.5 text-xs leading-relaxed text-teal-900 dark:text-teal-200">
                <div className="font-semibold text-teal-700 dark:text-teal-300 flex items-center gap-1.5 mb-1">
                  <CheckCircle2 className="size-3.5" />
                  Transparency Insights
                </div>
                <span>
                  {data?.salaryStats.percentage ?? 0}% of all current tech listings publish explicit salary ranges.
                  Pay disclosure is highest in US/Remote roles and California/New York postings.
                </span>
              </div>
            </div>

            <div className="mt-5 pt-3 border-t border-border/50 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Verified against {data?.salaryStats.withSalaryCount ?? 0} listings with stated comp
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs text-brand font-medium"
                render={<Link to="/jobs" search={{ hasSalary: true }} />}
              >
                Browse with salary <ArrowRight className="ml-1 size-3" />
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Bottom Conversion Action Banner */}
      <section className="relative mt-16 overflow-hidden rounded-3xl border border-border/80 bg-gradient-to-b from-card via-card/95 to-muted/40 p-8 sm:p-12 text-center shadow-sm">
        {/* Ambient background glow orbs */}
        <div
          className="pointer-events-none absolute -top-24 -right-24 size-80 sm:size-96 rounded-full bg-violet-500/10 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-24 -left-24 size-80 sm:size-96 rounded-full bg-emerald-500/10 blur-3xl"
          aria-hidden="true"
        />

        {/* Large Decorative Icon Watermark Overlays */}
        <div
          className="pointer-events-none absolute -right-8 -bottom-10 select-none text-violet-500/[0.07] dark:text-violet-400/[0.08]"
          aria-hidden="true"
        >
          <TrendingUp className="size-64 sm:size-80 -rotate-12 stroke-[0.8]" />
        </div>
        <div
          className="pointer-events-none absolute -top-10 -left-10 select-none text-violet-500/[0.05] dark:text-violet-400/[0.06]"
          aria-hidden="true"
        >
          <Sparkles className="size-48 sm:size-60 rotate-12 stroke-[0.8]" />
        </div>

        {/* Eyebrow Tag */}
        <div className="relative inline-flex items-center gap-1.5 rounded-full border border-violet-500/25 bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-600 dark:text-violet-400 mb-4 shadow-xs">
          <Sparkles className="size-3.5" />
          <span>Turn Intelligence Into Interviews</span>
        </div>

        <h3 className="relative mx-auto max-w-2xl text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
          Match your resume against these in-demand positions
        </h3>

        <p className="relative mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          Don&apos;t guess what keywords you are missing. Benchmark your background against verified postings,
          check your live ATS match score, and tailor bullet points to target in-demand skills in seconds.
        </p>

        {/* Action Buttons */}
        <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3.5">
          <Button size="lg" render={<Link to="/resume-tailor" />} className="shadow-sm">
            <Wand2 className="mr-2 size-4" />
            Tailor My Resume
            <ArrowRight className="ml-1.5 size-3.5" />
          </Button>
          <Button variant="outline" size="lg" render={<Link to="/jobs" />}>
            <Search className="mr-2 size-4 text-muted-foreground" />
            Explore All {summary.totalActiveJobs.toLocaleString()} Jobs
          </Button>
        </div>

        {/* Value Prop Badges */}
        <div className="relative mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/50 pt-6 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5 font-medium">
            <CheckCircle2 className="size-3.5 text-emerald-500 shrink-0" />
            <span>Instant ATS keyword gaps</span>
          </div>
          <div className="flex items-center gap-1.5 font-medium">
            <CheckCircle2 className="size-3.5 text-emerald-500 shrink-0" />
            <span>100% verified employer listings</span>
          </div>
          <div className="flex items-center gap-1.5 font-medium">
            <CheckCircle2 className="size-3.5 text-emerald-500 shrink-0" />
            <span>3 free credits on signup</span>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
